import { uniqBy } from 'lodash';
import moment, { Moment } from 'moment-timezone';
import React, { useState } from 'react';
import {
  Form,
  FormInstance,
  TextInput,
  TextArea,
  RadioGroup,
  Switch,
  FormErrors
} from 'blueprint-form';
import { faToggleOn, faToggleOff } from '@fortawesome/pro-solid-svg-icons';
import { Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import Card, { CardBlock, CardTitle } from '../card';
import TagsInput from '../schedule/tags';
import Callout from '../callout';
import Timezone from '../schedule/timezone';
import Weekday from '../schedule/weekday';
import SelectTime from '../schedule/time';
import SelectContact from '../contact/select';
import ContactCCGroup from '../contact/cc';
import MultiTextArea from '../multi-textarea';
import { DateRangeInput } from '@blueprintjs/datetime';
import { TagType } from '../../../libs/tag';
import { GetWeekdaysInterval, FormatTime } from '../../../libs/time';
import {
  ContactParams,
  DigestFrequency,
  NotificationPreference,
  ContactCC
} from '../../../libs/contact';
import { GetPhoneFromInput } from '../../../libs/format';
import { REGEX_EMAIL } from '../../../libs/regex';
import { useTerms } from '../../../contexts/terms';

import {
  ContactData,
  AlertMode,
  ReportDigestFrequency,
  AlertDigestFrequency,
  ChatDigestFrequency,
  ContactCCData
} from './types';

// Helper to determine if a vacation starts today in the user's TZ
const isVacationStartingToday = (startDate?: string | null): boolean =>
  !!startDate && moment(startDate, 'YYYY-MM-DD').isSame(moment(), 'day');

export const SerializeContactData = (data: ContactData): ContactParams => {
  let start: Moment = null;
  if (data.primaryStartTime) {
    start = moment(data.primaryStartTime, 'HH:mm');
  }
  let end: Moment = null;
  if (data.primaryEndTime) {
    end = moment(data.primaryEndTime, 'HH:mm');
  }
  if (data.primaryAllDay) {
    start = moment('00:00', 'HH:mm');
    end = moment('23:59', 'HH:mm');
  }
  const primaryStartHour = start.hour();
  const primaryStartMinute = start.minute();
  const primaryEndHour = end.hour();
  const primaryEndMinute = end.minute();
  const primaryDays = data.primaryDays.join(',');
  const ccs: ContactCC[] = [];
  if (data.ccs && data.ccs.length > 0) {
    // Consolidate the CC entries
    const _ccs = uniqBy(data.ccs, (cc) => `${cc.email}.${cc.copyId}`);
    for (const cc of _ccs) {
      ccs.push({
        email: cc.email,
        copyId: cc.copyId
      });
    }
  }
  const isStartingToday = isVacationStartingToday(data.vacationStartDate);

  const params: ContactParams = {
    id: data.id,
    displayName: data.displayName,
    description: data.description,
    tz: data.tz,
    email: data.email,
    npi: data.npi,
    primaryPhone: data.primaryPhone,
    primaryExtension: data.primaryExtension || null,
    primaryStartHour,
    primaryStartMinute,
    primaryEndHour,
    primaryEndMinute,
    primaryDays,
    offHourPhone: data.offHourPhone,
    offHourExtension: data.offHourExtension || null,
    afterHoursMessages: data.afterHoursMessages || null,
    alertPhone: data.alertPhone || data.primaryPhone,
    alertExtension:
      (data.alertPhone ? data.alertExtension : data.primaryExtension) || null,
    alertStartHour:
      data.alertMode === AlertMode.AllTimes ? null : primaryStartHour,
    alertStartMinute:
      data.alertMode === AlertMode.AllTimes ? null : primaryStartMinute,
    alertEndHour: data.alertMode === AlertMode.AllTimes ? null : primaryEndHour,
    alertEndMinute:
      data.alertMode === AlertMode.AllTimes ? null : primaryEndMinute,
    alertDays: data.alertMode === AlertMode.AllTimes ? null : primaryDays,
    notificationPreference: data.notificationPreference,
    chatNotificationPreference: data.chatNotificationPreference,
    receiveReports: data.reportDigestFrequency !== ReportDigestFrequency.Off,
    reportDigestFrequency: data.reportDigestFrequency as DigestFrequency,
    alertDigestFrequency: data.alertDigestFrequency as DigestFrequency,
    chatDigestFrequency: data.chatDigestFrequency as DigestFrequency,
    tags: data.tags,
    ccs
  };

  if (data.isOnVacation && isStartingToday) {
    params.backupId = data.backupId;
    params.vacationEndDate = data.vacationEndDate || null;
  } else if (data.isOnVacation) {
    params.backupId = data.backupId;
    params.vacationStartDate = data.vacationStartDate || null;
    params.vacationEndDate = data.vacationEndDate || null;
  } else {
    params.vacationStartDate = data.vacationStartDate || null;
    params.vacationEndDate = data.vacationEndDate || null;
  }
  // Only send cancelVacation when true
  if (data.cancelVacation === true) {
    params.cancelVacation = true;
  }

  return params;
};

export const DeserializeContactData = (params: ContactParams): ContactData => {
  let start: Moment = moment('09:00', 'HH:mm');
  if (params.primaryStartHour != null && params.primaryStartMinute != null) {
    start = moment()
      .hour(params.primaryStartHour)
      .minute(params.primaryStartMinute);
  }
  let end: Moment = moment('18:00', 'HH:mm');
  if (params.primaryEndHour != null && params.primaryEndMinute != null) {
    end = moment().hour(params.primaryEndHour).minute(params.primaryEndMinute);
  }
  let primaryAllDay = false;
  if (
    params.primaryStartHour === 0 &&
    params.primaryStartMinute === 0 &&
    params.primaryEndHour === 23 &&
    params.primaryEndMinute === 59
  ) {
    primaryAllDay = true;
  }
  const ccs: ContactCCData[] = [];
  if (params.ccs) {
    for (const cc of params.ccs) {
      const isEmail = cc.copyId == null;
      ccs.push({
        id: cc.id,
        isEmail: isEmail,
        email: isEmail ? cc.email : '',
        copyId: cc.copyId
      });
    }
  }
  const data: ContactData = {
    id: params.id,
    displayName: params.displayName,
    description: params.description,
    tz: params.tz,
    email: params.email,
    npi: params.npi,
    primaryPhone: params.primaryPhone,
    primaryExtension: params.primaryExtension,
    primaryStartTime: start.format('HH:mm'),
    primaryEndTime: end.format('HH:mm'),
    primaryDays: params.primaryDays.split(',').map((day) => parseInt(day, 10)),
    primaryAllDay,
    offHourPhone: params.offHourPhone,
    offHourExtension: params.offHourExtension,
    afterHoursMessages: params.afterHoursMessages || {
      alert: [''],
      chat: ['']
    },
    alertPhone: params.alertPhone || params.primaryPhone,
    alertExtension: params.alertPhone
      ? params.alertExtension
      : params.primaryExtension,
    alertMode:
      params.alertDays == null
        ? AlertMode.AllTimes
        : AlertMode.BusinessHourOnly,
    notificationPreference: params.notificationPreference,
    chatNotificationPreference: params.chatNotificationPreference,
    alertDigestFrequency:
      params.alertDigestFrequency || AlertDigestFrequency.Off,
    reportDigestFrequency:
      params.reportDigestFrequency || ReportDigestFrequency.Off,
    chatDigestFrequency: params.chatDigestFrequency || ChatDigestFrequency.Off,
    ccs: ccs,
    isOnVacation: params.backupId != null,
    backupId: params.backupId,
    vacationStartDate: params.vacationStartDate || null,
    vacationEndDate: params.vacationEndDate || null,
    cancelVacation: params.cancelVacation || false,
    tags: params.tags
  };
  return data;
};

export const GetContactFormInstance = (
  initialData: ContactParams
): FormInstance<ContactData, ContactParams> => {
  const data = DeserializeContactData(initialData);
  return new FormInstance<ContactData, ContactParams>(data, {
    serialize: SerializeContactData,
    deserialize: DeserializeContactData
  });
};

const LeftColumn: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <Col className="mb-4" sm={12}>
    {children}
  </Col>
);

const RightColumn: React.FC<{ children?: React.ReactNode }> = ({
  children
}) => (
  <Col sm={10} offset={{ sm: 2 }}>
    {children}
  </Col>
);

interface BusinessHoursProps {
  days?: number[];
  start?: Date;
  end?: Date;
  showEdit?: boolean;
  onEdit?: (event: React.MouseEvent) => any;
}

const BusinessHours: React.FC<BusinessHoursProps> = ({
  days,
  start,
  end,
  showEdit,
  onEdit
}): JSX.Element => {
  const weekdays = days ? GetWeekdaysInterval(days) : 'Everyday';
  let times = 'All Day Long';
  if (start && end) {
    times = FormatTime(start) + ' - ' + FormatTime(end);
  }
  return (
    <Card>
      <b className="mr-4">{weekdays}</b>
      <span className="mr-4">{times}</span>
      {showEdit && (
        <Button bare variant="minimal" intent="primary" onClick={onEdit}>
          Edit
        </Button>
      )}
    </Card>
  );
};

export interface ContactFormProps {
  form: FormInstance<ContactData, ContactParams>;
  onSubmit?: (data: ContactParams) => any;
}

const ContactForm: React.FC<ContactFormProps> = ({
  form,
  onSubmit
}): JSX.Element => {
  const [isEditingHours, setIsEditingHours] = useState(false);
  const { terms } = useTerms();
  const toggleVacation = (isOn: boolean): void => {
    form.setFieldValue('isOnVacation', isOn);
    if (!isOn) {
      form.setFieldValue('vacationStartDate', null);
      form.setFieldValue('vacationEndDate', null);
      form.setFieldValue('cancelVacation', true);
    } else {
      form.setFieldValue('cancelVacation', false);
    }
  };
  const validate = (data: ContactData): FormErrors => {
    const errors: FormErrors = {};
    if (data.email) {
      if (!REGEX_EMAIL.test(data.email)) {
        errors.email = 'The email is not in valid format!';
      }
    }
    if (data.primaryPhone) {
      const phone = GetPhoneFromInput(data.primaryPhone);
      if (phone.length !== 10) {
        errors.primaryphone = 'The phone number is not in valid format!';
      }
    }
    if (data.offHourPhone) {
      const phone = GetPhoneFromInput(data.offHourPhone);
      if (phone.length !== 10) {
        errors.offHourPhone = 'The phone number is not in valid format!';
      }
    }
    if (data.alertPhone) {
      const phone = GetPhoneFromInput(data.alertPhone);
      if (phone.length !== 10) {
        errors.alertPhone = 'The phone number is not in valid format!';
      }
    }
    if (data.afterHoursMessages) {
      const messages = data.afterHoursMessages.alert;
      const emptyMessages =
        messages.length > 1 &&
        (messages.filter((element) => element === '') as string[]);
      const greaterThan160Messages = messages.filter(
        (element) => element.length > 160
      );
      if (emptyMessages.length > 0) {
        if (!errors.afterHoursMessages) {
          errors.afterHoursMessages = {};
        }
        for (let i = 0; i < messages.length; i++) {
          /**
           * Empty form field will be an array with just 1 empty string - ['']
           */
          if (messages.length === 1 && messages[0] === '') {
            break;
          }
          if (emptyMessages.includes(messages[i])) {
            errors.afterHoursMessages['alert'][i] =
              'You must include some text in each message!';
          }
        }
      }
      if (greaterThan160Messages.length > 0) {
        if (!errors.afterHoursMessages) {
          errors.afterHoursMessages = {};
        }
        for (let i = 0; i < messages.length; i++) {
          if (greaterThan160Messages.includes(messages[i])) {
            errors.afterHoursMessages['alert'][i] =
              'Each messages should be less than or equal to 160 characters.';
          }
        }
      }
    }
    return errors;
  };
  return (
    <CardBlock bare>
      <Form form={form} validate={validate} onSubmit={onSubmit}>
        {(props) => {
          const values = props.values;
          let start: Date = null;
          if (values.primaryStartTime) {
            start = moment(values.primaryStartTime, 'HH:mm').toDate();
          }
          let end: Date = null;
          if (values.primaryEndTime) {
            end = moment(values.primaryEndTime, 'HH:mm').toDate();
          }
          const shouldAskAlertPhone = [
            NotificationPreference.Call,
            NotificationPreference.Page
          ].includes(values.notificationPreference);
          return (
            <React.Fragment>
              <CardTitle>
                <h3>Basic Information</h3>
              </CardTitle>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <TextInput
                      label={`${terms.patient.capitalized}-Facing Name`}
                      name="displayName"
                      required
                      fill
                    />
                    <p className="bp5-text-muted">
                      {40 - values.displayName.length} Character(s) left
                    </p>
                    <p>
                      Use a name {terms.patient.plural} will recognize. They see
                      or hear this name when they get messages from the
                      programs.
                    </p>
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <h4 className="mt-0 bp5-text-muted">
                        Sample {terms.patient.capitalized}-Facing Message
                      </h4>
                      <p className="bp5-running-text">
                        Hi Taylor, this is a service of{' '}
                        <b>{values.displayName || '[Display Name]'}</b>. Reply
                        YES to receive messages about your health.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
                <Row>
                  <LeftColumn>
                    <TextArea
                      label="Describe This Contact for Internal Use"
                      name="description"
                      fill
                    />
                    <p>
                      This description could be the name of a specific teammate,
                      internal group, or name of the initiative.
                    </p>
                  </LeftColumn>
                </Row>
                <Row>
                  <LeftColumn>
                    <TagsInput
                      label="Add Tags (Optional)"
                      name="tags"
                      type={TagType.Contact}
                      fill
                    />
                  </LeftColumn>
                </Row>
              </CardBlock>
              <CardTitle>
                <h3>Contact Information</h3>
              </CardTitle>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <div className="bp5-form-group">
                      <label className="bp5-label">
                        Business Hours
                        <span className="bp5-text-muted"> (required)</span>
                        <input
                          type="hidden"
                          name="isEditingHours"
                          value={String(isEditingHours)}
                        />
                      </label>
                      {!isEditingHours && (
                        <BusinessHours
                          days={values.primaryDays}
                          start={start}
                          end={end}
                          showEdit
                          onEdit={() => setIsEditingHours(true)}
                        />
                      )}
                    </div>
                    <div className={!isEditingHours ? 'hidden' : null}>
                      <Weekday
                        label="Business Days"
                        name="primaryDays"
                        minItems={1}
                      />
                      <div>
                        <Switch label="Open All Day" name="primaryAllDay" />
                      </div>
                      {!values.primaryAllDay && (
                        <React.Fragment>
                          <SelectTime
                            label="Business Hours - Start Time"
                            name="primaryStartTime"
                          />
                          <SelectTime
                            label="Business Hours - End Time"
                            name="primaryEndTime"
                          />
                        </React.Fragment>
                      )}
                      <br />
                    </div>
                    <div>
                      <TextInput
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        required
                        fill
                      />
                    </div>
                    <Row>
                      <Col md={18} sm={16}>
                        <TextInput
                          label="Primary Phone Number"
                          name="primaryPhone"
                          autoComplete="tel"
                          required
                          fill
                        />
                      </Col>
                      <Col md={6} sm={8}>
                        <TextInput label="Ext." name="primaryExtension" fill />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={18} sm={16}>
                        <TextInput
                          label="After-Hours Phone"
                          name="offHourPhone"
                          autoComplete="tel"
                          required
                          fill
                        />
                      </Col>
                      <Col md={6} sm={8}>
                        <TextInput label="Ext." name="offHourExtension" fill />
                      </Col>
                    </Row>
                    <p>
                      If you do not have an after-hours phone, just use the same
                      number as the primary phone.
                    </p>
                    <Timezone label="Contact Timezone" name="tz" fill />
                    <TextInput
                      label="Contact Identifier (e.g. NPI)"
                      name="npi"
                      fill
                    />
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-running-text">
                        This contact information is not visible to{' '}
                        {terms.patient.plural}. However, if the{' '}
                        {terms.patient.default} calls the phone number from
                        which they are getting the calls/text messages, the{' '}
                        {terms.patient.default} will be redirected to the work
                        phone number or after-hours phone number provided here.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
                <Row>
                  <LeftColumn>
                    <MultiTextArea
                      formProps={props}
                      texts={props.values.afterHoursMessages.alert}
                      customDataValue="afterHoursMessages.alert"
                      customTitle="Custom After-Hours Messages For Alerts"
                    />
                    <MultiTextArea
                      formProps={props}
                      texts={props.values.afterHoursMessages.chat}
                      customDataValue="afterHoursMessages.chat"
                      customTitle="Custom After-Hours Messages For Chats"
                    />
                  </LeftColumn>
                  <RightColumn>
                    {/* Move down the Callout to match the title of the left column in this section */}
                    <div style={{ marginTop: '1.33em' }}>
                      <Callout className="mt-4" intent="primary">
                        <p className="bp5-running-text">
                          Note: business hours are not automatically included in
                          a custom after-hours message. If you wish to include
                          that information in your message(s), then you will
                          have to include that yourself somewhere in the
                          messaging.
                        </p>
                        <p className="bp5-running-text">
                          To go back to the standard after-hours message, click
                          on the "Clear Messages" button and then click on "Save
                          Contact" below.
                        </p>
                        <p className="bp5-running-text">
                          If you accidentally remove or clear a message, you can
                          just exit this page and the messages will reset to
                          what they were before.
                        </p>
                        <p className="bp5-running-text">
                          These messages are also used automatically when
                          patients text in during a communication suspension.
                        </p>
                      </Callout>
                    </div>
                  </RightColumn>
                </Row>
              </CardBlock>
              <CardTitle>
                <h3>Acute Alert Notifications</h3>
              </CardTitle>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <RadioGroup
                      className="mb-6"
                      label="Notification Method"
                      name="notificationPreference"
                      options={[
                        {
                          label: 'Email (Recommended)',
                          value: 'E'
                        },
                        {
                          label: 'Call',
                          value: 'C'
                        },
                        {
                          label: 'Integration (If Applicable)',
                          value: 'I'
                        },
                        {
                          label: 'Turn Off Notifications',
                          value: 'O'
                        }
                      ]}
                    />
                    {shouldAskAlertPhone && (
                      <Row>
                        <Col md={18} sm={16}>
                          <TextInput
                            label="Alert Phone Number"
                            name="alertPhone"
                            autoComplete="tel"
                            required
                            fill
                          />
                        </Col>
                        <Col md={6} sm={8}>
                          <TextInput label="Ext." name="alertExtension" fill />
                        </Col>
                      </Row>
                    )}
                    <RadioGroup
                      label="Notification Hours"
                      name="alertMode"
                      options={[
                        {
                          label: 'All Times - 24/7 (Recommended)',
                          value: 'all'
                        },
                        {
                          label: 'During Business Hours Only',
                          value: 'bh_only'
                        }
                      ]}
                    />
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-runing-text">
                        Condition-specific alert notifications are triggered and
                        sent in real-time based on {terms.patient.default}{' '}
                        responses. These notifications can be delivered by
                        email, call, or text.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
              </CardBlock>
              <CardTitle>
                <h3>Chat Notifications</h3>
              </CardTitle>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <RadioGroup
                      className="mb-6"
                      label="Notification Method"
                      name="chatNotificationPreference"
                      options={[
                        {
                          label: 'In App (Recommended)',
                          value: 'O'
                        },
                        {
                          label: 'Email',
                          value: 'E'
                        }
                      ]}
                    />
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-runing-text">
                        When a {terms.patient.default} sends a message in the
                        chat center, the In App setting will add an unread
                        message count to their chat. The Email setting maintains
                        the unread message count, but also will send an email
                        when a {terms.patient.default} sends a message. This is
                        on a 5 minute delay per message.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
              </CardBlock>
              <CardTitle>
                <h3>Copy Alerts & Chat Messages (CC)</h3>
              </CardTitle>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <ContactCCGroup
                      name="ccs"
                      excludeIds={values.id ? [values.id] : null}
                    />
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-runing-text">
                        Optionally, you may choose to{' '}
                        <b>
                          send a copy of the alert and unread chat emailing if
                          enabled
                        </b>{' '}
                        to other contacts. This can be useful if you are
                        managing {terms.patient.plural} via a group approach.
                        You may at max set 10 contacts to CC.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
              </CardBlock>
              <CardTitle>
                <h3>Email Digests</h3>
              </CardTitle>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <RadioGroup
                      label="Report Digest"
                      name="reportDigestFrequency"
                      options={[
                        {
                          label: 'Weekly (Recommended)',
                          value: 'W'
                        },
                        {
                          label: 'Every 3 Days',
                          value: 'T'
                        },
                        {
                          label: 'Every 2 Weeks',
                          value: 'B'
                        },
                        {
                          label: 'Off - No Report Digest',
                          value: 'O'
                        }
                      ]}
                    />
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-runing-text">
                        This report contains information about recent alerts and{' '}
                        {terms.patient.plural} with Red or Yellow statuses. It
                        could serve as a good top-of-mind communication for you.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <RadioGroup
                      label="After-Hours Alert Digest"
                      name="alertDigestFrequency"
                      options={[
                        {
                          label: 'Off - No Alert Digest',
                          value: 'O'
                        },
                        {
                          label: 'Daily',
                          value: 'D'
                        },
                        {
                          label: 'Every 3 Days',
                          value: 'T'
                        },
                        {
                          label: 'Weekly',
                          value: 'W'
                        }
                      ]}
                    />
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-runing-text">
                        If acute alerts occur after-hours, they may appear in a
                        digest sent the next day to this contact's email
                        Address. If after-hours alerts remain unresolved by the
                        next morning, those alerts will be sent in a digest
                        email the following day.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    <RadioGroup
                      label="Chat Digest"
                      name="chatDigestFrequency"
                      options={[
                        {
                          label: 'Off - No Chat Digest',
                          value: 'O'
                        },
                        {
                          label: 'Daily',
                          value: 'D'
                        },
                        {
                          label: 'Every 3 Days',
                          value: 'T'
                        },
                        {
                          label: 'Weekly',
                          value: 'W'
                        }
                      ]}
                    />
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-runing-text">
                        This report contains information about all chat messages
                        from {terms.patient.plural}.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
              </CardBlock>
              <CardTitle>
                <h3>Vacation Mode</h3>
              </CardTitle>
              <CardBlock>
                <Row>
                  <LeftColumn>
                    {values.isOnVacation ? (
                      <React.Fragment>
                        <SelectContact
                          label="Select Backup Contact"
                          name="backupId"
                          excludeIds={values.id ? [values.id] : null}
                          required
                          fill
                        />
                        <div className="mt-4 bp5-form-group">
                          <p className="mb-2 bp5-text-muted">
                            Vacation Date Range
                          </p>
                          <DateRangeInput
                            formatDate={(date) =>
                              moment(date).format('YYYY-MM-DD')
                            }
                            parseDate={(str) =>
                              moment(str, 'YYYY-MM-DD').toDate()
                            }
                            value={[
                              values.vacationStartDate
                                ? moment(
                                    values.vacationStartDate,
                                    'YYYY-MM-DD'
                                  ).toDate()
                                : null,
                              values.vacationEndDate
                                ? moment(
                                    values.vacationEndDate,
                                    'YYYY-MM-DD'
                                  ).toDate()
                                : null
                            ]}
                            minDate={moment().startOf('day').toDate()}
                            closeOnSelection
                            onChange={([start, end]) => {
                              form.setFieldValue(
                                'vacationStartDate',
                                start
                                  ? moment(start).format('YYYY-MM-DD')
                                  : null
                              );
                              form.setFieldValue(
                                'vacationEndDate',
                                end ? moment(end).format('YYYY-MM-DD') : null
                              );
                            }}
                          />
                        </div>
                        <Button
                          className="mt-6"
                          icon={<Icon icon={faToggleOff} />}
                          onClick={() => toggleVacation(false)}
                        >
                          Turn Off Vacation Mode
                        </Button>
                      </React.Fragment>
                    ) : (
                      <Button
                        intent="primary"
                        icon={<Icon className="text-white" icon={faToggleOn} />}
                        onClick={() => toggleVacation(true)}
                      >
                        Turn On Vacation Mode
                      </Button>
                    )}
                  </LeftColumn>
                  <RightColumn>
                    <Callout intent="primary">
                      <p className="bp5-runing-text">
                        If the person managing this contact is temporarily
                        unavailable (e.g. go on vacation), turn on the Vacation
                        Mode and select another contact as the backup. When
                        Vacation Mode is on, any alerts intended to this contact
                        will be forwarded to the backup contact instead. This
                        also respects any CC settings on the backup contact.
                      </p>
                    </Callout>
                  </RightColumn>
                </Row>
              </CardBlock>
            </React.Fragment>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default ContactForm;
